import { Injectable, EventEmitter, Output } from '@angular/core';

import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { Observable } from 'rxjs/observable';
import { forkJoin } from 'rxjs/observable/forkJoin';
import 'rxjs/add/operator/finally';

import { environment } from '../../../environments/environment';
import { DataService } from './data.service';
import { TranslateService } from '@ngx-translate/core';

import { OfferEntryData, GridRow, DataRow, ContextMenuItem } from '../models/offerEntryData';

import { GetData } from '../../offer-entry-mock-data';
import { HttpHeaders } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { UtilsService } from './utils.service';

import * as cloneDeep from 'lodash/cloneDeep';
import { Product } from '../../shared/models/product';

@Injectable()
export class OfferEntryDataService {
    private useMockData = false;
    private isMetadataLoaded = false;
    private _metadata: any;
    get metadata() {
        return this._metadata;
    }

    private oData: OfferEntryData = {
        columnHeaders: [],
        columnEditable: [],
        intervalTimes: [],
        intervalIds: [],
        isOfferedToMarket: [],
        gridRows: [],
        sortedGridRows: [],
        sortOptions: [],
        selectedSort: null,
        additionalChartSeries: [],
        loadedPortfolioID: '',
        startTime: null,
        endTime: null,
        level: null,
        rowHeaderContextMenu: null,
        columnHeaderContextMenu: null,
        cellContextMenu: null,
        product: ''
    };
    private offerEntryData;
    private currentDate;
    private preBiddingDates;
    private currentDateLoaded: boolean = false;
    private offerEntryDataLoaded: boolean = false;

    private _level: string;

    private _startDate: string;
    get startDate() {
        return this._startDate;
    }

    private _endDate: string;
    get endDate() {
        return this._endDate;
    }

    private _portfolioId: string;
    get portfolioId() {
        return this._portfolioId;
    }

    private _productCode: string;
    get productCode() {
        return this._productCode;
    }

    private _productName: string;
    get productName() {
        return this._productName;
    }

    get isAutoCollapseEnabled(): boolean {
        return this._metadata['program_metadata']['auto_collapse_enabled_yn'] === 'Y';
    }

    constructor(private data: DataService,
                private translate: TranslateService,
                private spinnerService: SpinnerService,
                private utilsService: UtilsService
    ) {
        this.init();
    }

    init() {
        this.isMetadataLoaded = false;
        this._metadata = null;

        if (this.offerEntryData) {
            this.offerEntryData.next(this.oData);
        } else {
            this.offerEntryData = new BehaviorSubject<OfferEntryData>(this.oData);
        }

        if (this.currentDate) {
            this.currentDate.next(null);
        } else {
            this.currentDate = new BehaviorSubject<OfferEntryData>(null);
        }

        if (this.preBiddingDates) {
            this.preBiddingDates.next(null);
        } else {
            this.preBiddingDates = new BehaviorSubject<any>(null);
        }

        this.currentDateLoaded = false;
        this.offerEntryDataLoaded = false;

        this._startDate = null;
        this._endDate = null;
        this._portfolioId = null;
        this._productCode = null;
    }

    private getHeader(): HttpHeaders {
        let headers = new HttpHeaders();
        const programId = sessionStorage.getItem('currentProgram');
        this.utilsService.debugLog('Header Program: ' + programId);
        if (programId) {
            headers = headers.set('Program', programId);
        }
        return headers;
    }

    private setHeader(program: string): HttpHeaders {
        const headers = new HttpHeaders();
        return headers.set('Program', program);
    }

    loadCurrentDate() {
        if (!this.useMockData) {
            const url = environment.current_date_url;
            this.spinnerService.show('offerSpinner');
            this.data.get(url, this.getHeader())
                .finally(() => {
                    this.spinnerService.hide('offerSpinner');
                })
                .subscribe((dateResponse) => {
                    this.utilsService.debugLog('dateResponse: ', dateResponse);
                    if (dateResponse && dateResponse['data'] != null) {
                        this.currentDateLoaded = true;
                        this.currentDate.next(new Date(dateResponse['data'][0].trading_date));
                        this.preBiddingDates.next(dateResponse['data'][0]['pre_bidding_dates']);
                    }
                });
        } else {
            this.currentDateLoaded = true;
            this.currentDate.next(new Date());
        }
    }

    getCurrentDate(): Observable<Date> {
        return this.currentDate.asObservable();
    }

    getPreBiddingDates(): Observable<any> {
        return this.preBiddingDates.asObservable();
    }

    public loadProductData(program: string): Observable<any> {
        const url = environment.program_metadata_api_url + 'products';

        this.utilsService.debugLog('Invoking ' + url);
        this.spinnerService.show('offerSpinner');
        return this.data.get<any>(url, this.setHeader(program))
            .finally(() => {
                this.spinnerService.hide('offerSpinner');
            })
            .map((metadataResponse) => {
                if (metadataResponse && metadataResponse['data']) {
                    return metadataResponse['data'];
                }
                return metadataResponse;
            });
    }

    private loadMetaData(): Observable<any> {
        const url = environment.grid_metadata_url;

        this.utilsService.debugLog(`Invoking ${url}`);
        this.spinnerService.show('offerSpinner');
        return this.data.get<any>(url, this.getHeader())
            .finally(() => {
                this.spinnerService.hide('offerSpinner');
            })
            .map((metadataResponse) => {
                if (metadataResponse && metadataResponse['data']) {
                    return metadataResponse['data'];
                }
                return metadataResponse;
            });
    }

    loadOfferEntryData(subId: string, level: string, startDate: string, endDate: string, product: Product): boolean {
        let changed: boolean = false;
        if (this._startDate !== startDate || this._endDate !== endDate || this._portfolioId !== subId || this._productCode !== product.productCode) {
            changed = true;
        }
        this._startDate = startDate;
        this._endDate = endDate;
        this._portfolioId = subId;
        this._level = level;
        this._productCode = product.productCode;
        this._productName = product.name;

        if (!this.useMockData) {
            // const url = environment.grid_metadata_url;
            if (!this.isMetadataLoaded) {
                this.loadMetaData().subscribe((metadataResponse) => {
                    this.utilsService.debugLog('metadataResponse: ', metadataResponse);
                    if (metadataResponse != null) {
                        this._metadata = metadataResponse;
                        this.isMetadataLoaded = true;
                        this.loadOfferEntryGridData(subId, level, startDate, endDate, product.productCode, changed);
                    }
                });
            } else {
                this.loadOfferEntryGridData(subId, level, startDate, endDate, product.productCode, changed);
            }
        } else {
            this.offerEntryDataLoaded = true;
            this.offerEntryData.next(GetData());
        }

        return changed;
    }

    isGridDataLoaded() {
        return this.offerEntryDataLoaded;
    }

    sendNotification(id: string) {
        const body = {
            id: id
        };

        const endpoint = environment.notification_url;
        this.utilsService.debugLog('sending notification');
        this.utilsService.debugLog(body);
        this.data.post(endpoint, body, this.getHeader())
            .map((response) => {
                if (response) {
                    this.utilsService.debugLog('received response');
                    this.utilsService.debugLog(response);
                }
            }).subscribe(data => {
            // do something, if upload success
        }, error => {
        });
    }

    setBidPeriods(id: string, startDate: string, intervals: number[], product: string): Observable<any> {
        let url = environment.create_offer_url;
        url = url + '?action=OFFERPERIOD&trading_date=' + startDate + '&portfolio_id=' + id + '&product=' + product + '&intervals=' + intervals.join(',');

        this.utilsService.debugLog('setting bid period');
        this.spinnerService.show('offerSpinner');
        return this.data.get(url, this.getHeader())
            .map((response) => {
                if (response) {
                    this.utilsService.debugLog('received set bid period response');
                    this.utilsService.debugLog(response);
                }
            })
            .finally(() => {
                this.reloadGridData();  // load data again so we see the new colours for bid region
                this.spinnerService.hide('offerSpinner');
            });
    }

    createOffer(id: string, startDate: string, intervals: number[], product: string): Observable<any> {
        let url = environment.create_offer_url;
        url = url + '?action=SUBMIT&trading_date=' + startDate + '&portfolio_id=' + id + '&product=' + product + '&intervals=' + intervals.join(',');

        this.utilsService.debugLog('creating offer - ' + url);
        this.spinnerService.show('offerSpinner');
        return this.data.get(url, this.getHeader())
            .map((response: any) => {
                if (response) {
                    this.utilsService.debugLog('received response');
                    this.utilsService.debugLog(response);
                    if (response['data'] != null) {
                        let responseData: any = response['data'];
                        const fileName = responseData.file_name;
                        const fileData = this.b64toBlob(responseData.file_contents);
                        setTimeout(function () {
                            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                                window.navigator.msSaveOrOpenBlob(fileData, fileName);
                            } else {
                                const objectURL = window.URL.createObjectURL(fileData);
                                const anchor = document.createElement('a');
                                anchor.setAttribute('style', 'display: none');
                                anchor.href = objectURL;
                                anchor.setAttribute('download', fileName);
                                document.body.appendChild(anchor);
                                anchor.click();
                                this.utilsService.debugLog('downloaded ' + fileName);
                                setTimeout(function () {
                                    document.body.removeChild(anchor);
                                    window.URL.revokeObjectURL(objectURL);
                                    anchor.remove(); // remove the element
                                }, 100);
                            }
                        }, 0);

                        const regFileName = responseData.file_name_registrations;
                        const regFileData = this.b64toBlob(responseData.registration_file_contents);
                        setTimeout(function () {
                            if (window.navigator && window.navigator.msSaveOrOpenBlob) {
                                window.navigator.msSaveOrOpenBlob(regFileData, regFileName);
                            } else {
                                const objectURL = window.URL.createObjectURL(regFileData);
                                const anchor = document.createElement('a');
                                anchor.setAttribute('style', 'display: none');
                                anchor.href = objectURL;
                                anchor.setAttribute('download', regFileName);
                                document.body.appendChild(anchor);
                                anchor.click();
                                this.utilsService.debugLog('downloaded ' + regFileName);
                                setTimeout(function () {
                                    document.body.removeChild(anchor);
                                    window.URL.revokeObjectURL(objectURL);
                                    anchor.remove(); // remove the element
                                }, 100);
                            }
                        }, 500);
                    }
                }
            })
            .finally(() => {
                this.reloadGridData();  // load data again so we see the new colours for bid region
                this.spinnerService.hide('offerSpinner');
            });
    }

    b64toBlob(b64Data) {
        const contentType = '';
        const sliceSize = 512;

        const byteCharacters = atob(b64Data);
        const byteArrays = [];

        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice = byteCharacters.slice(offset, offset + sliceSize);

            const byteNumbers = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }

            const byteArray = new Uint8Array(byteNumbers);

            byteArrays.push(byteArray);
        }

        const blob = new Blob(byteArrays, {type: contentType});
        return blob;
    }

    confirmClearedQuantity(
        portfolioId: string, equipmentId: string, actionType: string, level: string, start_datetime: string, end_datetime: string, product: string
    ): Observable<any> {
        const body = {
            portfolio_id: portfolioId,
            equipment_id: equipmentId,
            action_type: actionType,
            level: level,
            start_datetime: start_datetime,
            end_datetime: end_datetime,
            interval_id: [],
            data_value: [],
            product: product
        };

        const endpoint = environment.offer_equipment_url;
        return this.data.post(endpoint, body, this.getHeader())
            .map((response) => {
                if (response) {
                    this.utilsService.debugLog('received response');
                    this.utilsService.debugLog(response);
                }
            }).finally(() => {
                this.reloadGridData();  // reload the data again so we see the cascading/prorated changes
            });
    }

    noClearedOffer(portfolioId: string, offerDate: string, product: string): Observable<any> {
        const endpoint = environment.no_cleared_offer_url + `?portfolio_id=${portfolioId}&offer_date=${offerDate}&product=${product}`;
        return this.data.post(endpoint, {}, this.getHeader())
            .map((response) => {
                if (response) {
                    this.utilsService.debugLog('No Cleared Offer!');
                    this.utilsService.debugLog(response);
                }
            }).finally(() => {
                this.reloadGridData();
            });
    }

    saveDataChangesToDatabase(level: string, id: string, dataType: string, date: string, intervals: number[], values: number[], product: string): Observable<any> {
        const body = {
            level: level,
            id: id,
            data_type: dataType,
            date: date,
            interval_id: intervals,
            data_value: values,
            product
        };
        this.utilsService.debugLog('Saving ' + values.join(',') + ' to ' + intervals.join(','));
        const endpoint = environment.grid_data_url;
        this.utilsService.debugLog('saving change');
        this.utilsService.debugLog(body);
        // this.utilsService.debugLog(base64);
        return this.data.post(endpoint, body, this.getHeader())
            .map((response) => {
                if (response) {
                    this.utilsService.debugLog('received response');
                    this.utilsService.debugLog(response);
                }
            }).finally(() => {
                this.reloadGridData();
            });
    }

    reloadGridData() {
        this.loadOfferEntryGridData(this._portfolioId, this._level, this.startDate, this.endDate, this._productCode, false);  // reload grid
    }

    private loadOfferEntryGridData(subId: string, level: string, startDate: string, endDate: string, product: string, changed: boolean) {
        let url = environment.grid_data_url;
        url = url + '?level=' + level + '&start_datetime=' + startDate + '&end_datetime=' + endDate + '&product=' + product;
        if (subId.length > 0) {
            url = url + '&portfolio_id=' + subId;
        }

        this.utilsService.debugLog('Invoking ' + url);
        if (changed) {
            this.spinnerService.show('offerSpinner');
        }
        this.data.get(url, this.getHeader())
            .finally(() => {
                if (changed) {
                    this.spinnerService.hide('offerSpinner');
                }
            })
            .subscribe((dataResponse) => {
                this.utilsService.debugLog('dataResponse: ', dataResponse);
                if (dataResponse != null) {
                    this.offerEntryDataLoaded = true;
                    const loadedOfferEntryData = this.decodeOfferEntryDataResponse(dataResponse['data'], product);
                    this.offerEntryData.next(cloneDeep(loadedOfferEntryData));
                }
            });
    }

    loadNewOfferGridData(portfolioId: string, equipmentList: any[], startDate: string, endDate: string, product: string) {
        this.spinnerService.show('offerSpinner');

        let url = environment.offer_equipment_cbl_url;
        url = url + `?portfolio_id=${portfolioId}&start_datetime=${startDate}&end_datetime=${endDate}&product=${product}`;

        const httpRequests = [];
        for (const equipment of equipmentList) {
            const endpoint = `${url}&equipment_id=${equipment.id}`;
            httpRequests.push(this.data.get(endpoint, this.getHeader()).map((response) => ({
                success: true,
                response: response,
                equipment: equipment,
                data: this.decodeNewOfferDataResponse(response['data'], equipment, product)
            })).catch(error => (Observable.of({
                success: false,
                response: error,
                equipment: equipment
            }))));
        }

        return forkJoin(httpRequests).finally(() => {
            this.spinnerService.hide('offerSpinner');
        });
    }

    getOfferEntryData(): Observable<OfferEntryData> {
        return this.offerEntryData.asObservable();
    }

    round(num: number): number {
        if (num) {
            return Math.round(num);
        } else {
            return num;
        }
    }

    private getContextMenuItems(componentKey: string): ContextMenuItem[] {
        const contextMenus: ContextMenuItem[] = [];
        if (!this._metadata) {
            return;
        }
        if (!this._metadata.hasOwnProperty('context_menu_metadata')) {
            this.utilsService.debugLog('Expected property \'context_menu_metadata\' was not found in the grid metadata');
            return contextMenus;
        }
        if (this._metadata.context_menu_metadata.hasOwnProperty(componentKey)) {
            for (const cmItem of this._metadata.context_menu_metadata[componentKey]) {
                let nxtItem: ContextMenuItem = {
                    typeKey: cmItem.type_key,
                    functionKey: cmItem.function_key,
                    caption: cmItem.caption,
                    isSeparator: cmItem.is_separator,
                    includeForAggregatedView: cmItem.include_for_aggregated_view,
                    includeForDetailedView: cmItem.include_for_detailed_view
                };
                contextMenus.push(nxtItem);
            }
        }
        return contextMenus;
    }

    private decodeOfferEntryDataResponse(dataResponse, product: string): OfferEntryData {
        const offerEntryData: OfferEntryData = {
            columnHeaders: [],
            columnEditable: [],
            intervalTimes: [],
            intervalIds: [],
            isOfferedToMarket: [],
            gridRows: [],
            sortedGridRows: [],
            sortOptions: [],
            selectedSort: null,
            additionalChartSeries: [],
            loadedPortfolioID: '',
            startTime: null,
            endTime: null,
            level: null,
            rowHeaderContextMenu: null,
            columnHeaderContextMenu: null,
            cellContextMenu: null,
            product: ''
        };

        offerEntryData.rowHeaderContextMenu = this.getContextMenuItems('row_header');
        offerEntryData.columnHeaderContextMenu = this.getContextMenuItems('column_header');
        offerEntryData.cellContextMenu = this.getContextMenuItems('grid_cell');

        offerEntryData.level = dataResponse.level;
        offerEntryData.startTime = new Date(dataResponse.start_date);
        offerEntryData.endTime = new Date(dataResponse.end_date);
        offerEntryData.product = product;

        for (const columnMetadata of this._metadata.column_metadata) {
            offerEntryData.columnHeaders.push(columnMetadata.caption);
            offerEntryData.columnEditable.push(columnMetadata.is_editable);
            offerEntryData.intervalTimes.push(
                new Date(
                    offerEntryData.startTime.getFullYear(),
                    offerEntryData.startTime.getMonth(),
                    offerEntryData.startTime.getDate(),
                    columnMetadata.interval_time.split(':')[0],
                    columnMetadata.interval_time.split(':')[1],
                    columnMetadata.interval_time.split(':')[2]
                ));
            offerEntryData.intervalIds.push(columnMetadata.interval_id);
            offerEntryData.isOfferedToMarket.push(false);
        }

        if (dataResponse.is_offered_to_market) {
            for (let i = 0; i < dataResponse.is_offered_to_market.length; i++) {
                offerEntryData.isOfferedToMarket[i] = dataResponse.is_offered_to_market[i];
            }
        }

        if (dataResponse.portfolio_id) {
            offerEntryData.loadedPortfolioID = dataResponse.portfolio_id;
        }

        for (const row of dataResponse.data) {
            if (row.is_total_row) {
                offerEntryData.loadedPortfolioID = row.id;
            }

            let isSubGrid = false;
            if (row.hasOwnProperty('parent_id')) {
                if (row.parent_id.length > 0) {
                    isSubGrid = true;
                }
            }

            const datarows: DataRow[] = [];
            if (this._metadata.row_metadata.hasOwnProperty(row.metadata_key)) {
                for (const metadataRow of this._metadata.row_metadata[row.metadata_key]) {
                    const dataArray: number[] = [];           // this includes user's changes
                    const roundedDataArray: number[] = [];
                    const cellColourArray: string[] = [];
                    const participationColourArray: number[] = [];
                    const cellTooltipArray: string[] = [];
                    const fontColourArray: string[] = [];
                    const shadeColourArray: string[] = [];

                    for (const colHeader of offerEntryData.columnHeaders) {
                        dataArray.push(null);
                        roundedDataArray.push(null);
                        cellColourArray.push('');
                        cellTooltipArray.push('');
                        fontColourArray.push('');
                        shadeColourArray.push('');
                    }

                    if (dataResponse.interval_ids) {
                        dataResponse.interval_ids.forEach((interval, index) => {
                            const arrayIx = offerEntryData.intervalIds.indexOf(interval);
                            if (arrayIx < 0) {
                                this.utilsService.debugLog('Unexpected interval received');
                            } else {
                                if (row[metadataRow.data_array_key]) {
                                    dataArray[arrayIx] = row[metadataRow.data_array_key][index];
                                    roundedDataArray[arrayIx] = this.round(row[metadataRow.data_array_key][index]);
                                }
                                if (row[metadataRow.colour_array_key]) {
                                    cellColourArray[arrayIx] = row[metadataRow.colour_array_key][index];
                                }
                                if (row[metadataRow.top_cell_indicator_key]) {
                                    participationColourArray[arrayIx] = row[metadataRow.top_cell_indicator_key][index];
                                }
                                if (row[metadataRow.cell_tooltip_key]) {
                                    cellTooltipArray[arrayIx] = row[metadataRow.cell_tooltip_key][index];
                                }
                                if (row[metadataRow.font_colour_key]) {
                                    fontColourArray[arrayIx] = row[metadataRow.font_colour_key][index];
                                }
                                if (row[metadataRow.shade_colour_key]) {
                                    shadeColourArray[arrayIx] = row[metadataRow.shade_colour_key][index];
                                }
                            }
                        });
                    }

                    const datarow: DataRow = {
                        name: metadataRow.caption,
                        metaDataKey: metadataRow.data_array_key,
                        isEditable: metadataRow.is_editable,
                        showOnlyOnExpand: metadataRow.is_collapsed,
                        isOnChart: metadataRow.is_charted,
                        lineColour: metadataRow.line_colour,
                        lineCaption: metadataRow.line_caption,
                        isVisibleInGrid: metadataRow.show_in_grid,
                        typeOnChart: metadataRow.chart_style,
                        tooltip: metadataRow.tooltip,
                        dataValues: dataArray,
                        roundedDataValues: roundedDataArray,
                        rowSum: 0,
                        cellColours: cellColourArray,
                        participationIndicators: participationColourArray,
                        cellTooltip: cellTooltipArray,
                        fontColours: fontColourArray,
                        shadeColours: shadeColourArray,
                        showStatus: metadataRow.showStatus // This is undefined //fix
                    };

                    // sum each row for the combined sorts
                    datarow.dataValues.forEach((val) => {
                        datarow.rowSum = datarow.rowSum + (+val);
                    });
                    datarows.push(datarow);
                }
                const subGridRows: string[] = [];
                if (row.hasOwnProperty('sub_grid_rows')) {
                    for (const subGridRow of row.sub_grid_rows) {
                        subGridRows.push(subGridRow);
                    }
                }

                let manualUpdates: boolean[] = [];
                if (row.updated_manually) {
                    manualUpdates = row.updated_manually.map(m => m !== 0);
                }

                // add sort row for offer
                const sortrow: DataRow = {
                    name: this.translate.instant('default-text.offer-status'),
                    metaDataKey: '',
                    isEditable: false,
                    showOnlyOnExpand: false,
                    isOnChart: false,
                    lineColour: '',
                    lineCaption: '',
                    isVisibleInGrid: false,
                    typeOnChart: '',
                    tooltip: '',
                    dataValues: [],
                    roundedDataValues: [],
                    rowSum: row.state_order,  // for sorting
                    cellColours: [],
                    participationIndicators: [],
                    cellTooltip: [],
                    fontColours: [],
                    shadeColours: [],
                    showStatus: false
                };
                datarows.push(sortrow);

                const gridrow: GridRow = {
                    id: row.id,
                    name: row.name,
                    aggregationlevel: row.aggregation_level,
                    deratingFactor: row.derating_factor,
                    deratingFactorEditField: row.derating_factor,
                    paymentCapPercent: row.payment_cap_percent,
                    hasAlert: row.has_alert,
                    alertTooltip: row.alert_tooltip,
                    alertColor: row.alert_color,
                    hasInfo: row.has_info,
                    infoTooltip: row.info_tooltip,
                    stateOrder: row.state_order,
                    stateCode: row.state_code,
                    subGridRows: subGridRows,
                    isVisibleOnGrid: true, // todo
                    isVisibleOnChart: !isSubGrid,
                    chartColour: row.chart_color,
                    hasManualUpdates: manualUpdates,
                    subExpanded: false,
                    isTotalRow: row.is_total_row,
                    isSubGrid: isSubGrid,
                    isSubGridAndIsVisible: false,
                    bidExpanded: false,
                    dataRows: datarows,
                };

                if (row.cleared_intervals) {
                    gridrow.clearedIntervals = row.cleared_intervals;
                }

                offerEntryData.gridRows.push(gridrow);
            } else {
                this.utilsService.debugLog(row.metadata_key + ' not found in meta data');
            }
        }

        // this.utilsService.debugLog('decoded data: ');
        // this.utilsService.debugLog(offerEntryData);
        return offerEntryData;
    }

    decodeNewOfferDataResponse(dataResponse: any, equipment: any, product: string): OfferEntryData {
        const offerEntryData: OfferEntryData = {
            columnHeaders: [],
            columnEditable: [],
            intervalTimes: [],
            intervalIds: [],
            isOfferedToMarket: [],
            gridRows: [],
            sortedGridRows: [],
            sortOptions: [],
            selectedSort: null,
            additionalChartSeries: [],
            loadedPortfolioID: '',
            startTime: null,
            endTime: null,
            level: null,
            rowHeaderContextMenu: null,
            columnHeaderContextMenu: null,
            cellContextMenu: null,
            product: ''
        };

        offerEntryData.level = 'REGISTRATION';
        offerEntryData.startTime = new Date(dataResponse.start_date);
        offerEntryData.endTime = new Date(dataResponse.end_date);
        offerEntryData.product = product;

        for (const columnMetadata of this.metadata.column_metadata) {
            offerEntryData.columnHeaders.push(columnMetadata.caption);
            offerEntryData.intervalTimes.push(
                new Date(
                    offerEntryData.startTime.getFullYear(),
                    offerEntryData.startTime.getMonth(),
                    offerEntryData.startTime.getDate(),
                    columnMetadata.interval_time.split(':')[0],
                    columnMetadata.interval_time.split(':')[1],
                    columnMetadata.interval_time.split(':')[2]
                ));
            offerEntryData.intervalIds.push(columnMetadata.interval_id);
        }

        if (dataResponse.is_offered_to_market) {
            for (let i = 0; i < dataResponse.is_offered_to_market.length; i++) {
                offerEntryData.isOfferedToMarket[i] = dataResponse.is_offered_to_market[i];
            }
        }

        if (dataResponse.portfolio_id) {
            offerEntryData.loadedPortfolioID = dataResponse.portfolio_id;
        }

        for (const row of dataResponse.data) {
            const datarows: DataRow[] = [];
            for (const metadataRow of this.metadata.row_metadata['detail_non_charted']) {
                const dataArray: number[] = [];           // this includes user's changes
                const roundedDataArray: number[] = [];           // this includes user's changes
                const colourArray: string[] = [];
                const cellTooltipArray: string[] = [];
                const fontColourArray: string[] = [];
                const shadeColourArray: string[] = [];

                const defaultData: number = (metadataRow.data_array_key === 'curtail_data') ? 0 : null;
                for (const colHeader of offerEntryData.columnHeaders) {
                    dataArray.push(defaultData);
                    roundedDataArray.push(defaultData);
                    colourArray.push('');
                    cellTooltipArray.push('');
                    fontColourArray.push('');
                    shadeColourArray.push('');
                }

                if (dataResponse.interval_ids) {
                    dataResponse.interval_ids.forEach((interval, index) => {
                        const arrayIx = offerEntryData.intervalIds.indexOf(interval);
                        if (arrayIx < 0) {
                            this.utilsService.debugLog('Unexpected interval received');
                        } else {
                            if (row[metadataRow.data_array_key]) {
                                dataArray[arrayIx] = row[metadataRow.data_array_key][index];
                            }
                        }
                    });
                }

                const isEditable: boolean = (metadataRow.data_array_key === 'cust_est_data') ? true : false;
                const datarow: DataRow = {
                    name: metadataRow.caption,
                    metaDataKey: metadataRow.data_array_key,
                    isEditable: isEditable,
                    showOnlyOnExpand: metadataRow.is_collapsed,
                    isOnChart: metadataRow.is_charted,
                    lineColour: metadataRow.line_colour,
                    lineCaption: metadataRow.line_caption,
                    isVisibleInGrid: true,
                    typeOnChart: metadataRow.chart_style,
                    tooltip: metadataRow.tooltip,
                    dataValues: dataArray,
                    roundedDataValues: this.getRoundedValues(dataArray),
                    rowSum: 0,
                    cellColours: colourArray,
                    cellTooltip: cellTooltipArray,
                    fontColours: fontColourArray,
                    shadeColours: shadeColourArray,
                    showStatus: metadataRow.showStatus // This is undefined //fix
                };

                // sum each row for the combined sorts
                datarow.dataValues.forEach((val) => {
                    datarow.rowSum = datarow.rowSum + (+val);
                });
                datarows.push(datarow);
            }

            const gridrow: GridRow = {
                id: (equipment) ? equipment.id : row.id,
                name: (equipment) ? equipment.sourceEquipmentId : row.name,
                deratingFactor: 1,
                deratingFactorEditField: 1,
                paymentCapPercent: 100,
                stateOrder: 2,
                stateCode: 'UNSUBMITTED',
                aggregationlevel: 'REGISTRATION',
                hasAlert: false,
                alertColor: '',
                alertTooltip: '',
                hasInfo: false,
                infoTooltip: '',
                subGridRows: [],
                subExpanded: true,
                isVisibleOnGrid: true, // todo
                isVisibleOnChart: true,
                chartColour: '#ffffff',
                hasManualUpdates: [],
                isTotalRow: false,
                isSubGrid: false,
                isSubGridAndIsVisible: false,
                bidExpanded: true,
                dataRows: datarows,
            };

            offerEntryData.gridRows.push(gridrow);
        }

        return offerEntryData;
    }

    getRoundedValues(dataValues: number[]): number[] {
        let roundedData: number[] = [];
        if (dataValues) {
            let i = dataValues.length;
            while (i--) {
                roundedData[i] = this.round(dataValues[i]);
            }
        }
        return roundedData;
    }

}



// WEBPACK FOOTER //
// ./src/app/core/services/offer-entry-data.service.ts